<template>
  <div
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
    @click.self="emit('close')"
  >
    <div
      class="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      @click.stop
    >
      <!-- Header -->
      <div class="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <h2 class="text-2xl font-bold text-gray-900">Cookie Preferences</h2>
        <button
          @click="emit('close')"
          class="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Content -->
      <div class="px-6 py-6 space-y-6">
        <p class="text-gray-600">
          <template v-if="isWhiteLabel">
            {{ shopName }} uses cookies to enhance your experience on this site. You can choose
            which categories of cookies you want to allow. Essential cookies are always enabled
            as they are necessary for the site to function properly.
          </template>
          <template v-else>
            We use cookies to enhance your experience on our website. You can choose which categories
            of cookies you want to allow. Essential cookies are always enabled as they are necessary
            for the website to function properly.
          </template>
        </p>

        <!-- Essential Cookies (Always On) -->
        <div class="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-3">
              <div class="w-12 h-6 bg-primary-600 rounded-full relative cursor-not-allowed opacity-60">
                <div class="absolute right-1 top-1 w-4 h-4 bg-white rounded-full"></div>
              </div>
              <h3 class="font-semibold text-gray-900">Essential Cookies</h3>
              <span class="text-xs text-gray-500 font-medium">Always Active</span>
            </div>
          </div>
          <p class="text-sm text-gray-600 mb-3">
            These cookies are necessary for the website to function and cannot be switched off.
            They are usually only set in response to actions made by you such as setting your privacy
            preferences, logging in, or filling in forms.
          </p>
          <details class="text-sm">
            <summary class="cursor-pointer text-primary-600 hover:text-primary-700 font-medium">
              What cookies are used?
            </summary>
            <ul class="mt-2 ml-4 space-y-1 text-gray-600 list-disc">
              <li><code class="bg-gray-100 px-1 py-0.5 rounded">auth_token</code> - Authentication session</li>
              <li><code class="bg-gray-100 px-1 py-0.5 rounded">refresh_token</code> - Session persistence</li>
              <li><code class="bg-gray-100 px-1 py-0.5 rounded">csrf_token</code> - Security protection</li>
              <li><code class="bg-gray-100 px-1 py-0.5 rounded">cookie_consent</code> - Your cookie preferences</li>
            </ul>
          </details>
        </div>

        <!-- Functional Cookies -->
        <div class="border border-gray-200 rounded-lg p-4">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-3">
              <button
                @click="localPreferences.functional = !localPreferences.functional"
                class="w-12 h-6 rounded-full relative transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                :class="localPreferences.functional ? 'bg-primary-600' : 'bg-gray-300'"
              >
                <div
                  class="absolute top-1 w-4 h-4 bg-white rounded-full transition-all duration-200"
                  :class="localPreferences.functional ? 'right-1' : 'left-1'"
                ></div>
              </button>
              <h3 class="font-semibold text-gray-900">Functional Cookies</h3>
            </div>
          </div>
          <p class="text-sm text-gray-600 mb-3">
            These cookies enable enhanced functionality and personalization, such as remembering your
            preferences (language, timezone, dark mode) and providing enhanced features.
          </p>
          <details class="text-sm">
            <summary class="cursor-pointer text-primary-600 hover:text-primary-700 font-medium">
              What cookies are used?
            </summary>
            <ul class="mt-2 ml-4 space-y-1 text-gray-600 list-disc">
              <li><code class="bg-gray-100 px-1 py-0.5 rounded">language</code> - Language preference</li>
              <li><code class="bg-gray-100 px-1 py-0.5 rounded">timezone</code> - Timezone preference</li>
              <li><code class="bg-gray-100 px-1 py-0.5 rounded">dark_mode</code> - Theme preference</li>
              <li><code class="bg-gray-100 px-1 py-0.5 rounded">currency</code> - Currency display</li>
            </ul>
          </details>
        </div>

        <!-- Analytics Cookies -->
        <div class="border border-gray-200 rounded-lg p-4">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-3">
              <button
                @click="localPreferences.analytics = !localPreferences.analytics"
                class="w-12 h-6 rounded-full relative transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                :class="localPreferences.analytics ? 'bg-primary-600' : 'bg-gray-300'"
              >
                <div
                  class="absolute top-1 w-4 h-4 bg-white rounded-full transition-all duration-200"
                  :class="localPreferences.analytics ? 'right-1' : 'left-1'"
                ></div>
              </button>
              <h3 class="font-semibold text-gray-900">Analytics Cookies</h3>
            </div>
          </div>
          <p class="text-sm text-gray-600 mb-3">
            These cookies help us understand how visitors interact with our website by collecting
            and reporting information anonymously. This helps us improve our website and services.
          </p>
          <details class="text-sm">
            <summary class="cursor-pointer text-primary-600 hover:text-primary-700 font-medium">
              What cookies are used?
            </summary>
            <ul class="mt-2 ml-4 space-y-1 text-gray-600 list-disc">
              <li><code class="bg-gray-100 px-1 py-0.5 rounded">_ga</code> - Google Analytics (if enabled)</li>
              <li><code class="bg-gray-100 px-1 py-0.5 rounded">_gid</code> - Google Analytics (if enabled)</li>
              <li><code class="bg-gray-100 px-1 py-0.5 rounded">usage_stats</code> - Internal analytics</li>
            </ul>
          </details>
        </div>

        <!-- Marketing Cookies -->
        <div class="border border-gray-200 rounded-lg p-4">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-3">
              <button
                @click="localPreferences.marketing = !localPreferences.marketing"
                class="w-12 h-6 rounded-full relative transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                :class="localPreferences.marketing ? 'bg-primary-600' : 'bg-gray-300'"
              >
                <div
                  class="absolute top-1 w-4 h-4 bg-white rounded-full transition-all duration-200"
                  :class="localPreferences.marketing ? 'right-1' : 'left-1'"
                ></div>
              </button>
              <h3 class="font-semibold text-gray-900">Marketing Cookies</h3>
            </div>
          </div>
          <p class="text-sm text-gray-600 mb-3">
            These cookies track your online activity to help advertisers deliver more relevant advertising
            or to limit how many times you see an ad. They may be set by us or by third-party providers.
          </p>
          <details class="text-sm">
            <summary class="cursor-pointer text-primary-600 hover:text-primary-700 font-medium">
              What cookies are used?
            </summary>
            <ul class="mt-2 ml-4 space-y-1 text-gray-600 list-disc">
              <li><code class="bg-gray-100 px-1 py-0.5 rounded">_fbp</code> - Facebook Pixel (if enabled)</li>
              <li><code class="bg-gray-100 px-1 py-0.5 rounded">_gcl_au</code> - Google Ads (if enabled)</li>
              <li><code class="bg-gray-100 px-1 py-0.5 rounded">ad_tracking</code> - Ad campaign tracking</li>
            </ul>
          </details>
        </div>

        <!-- Privacy / Cookie Policy links — WL routes them to the shop's About
             page (where the shop's terms/policies live); canonical Zaveato
             keeps the dedicated marketplace policy pages. -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p class="text-sm text-blue-900" v-if="isWhiteLabel">
            For more information about how cookies are used here and how your data is handled,
            please read our
            <router-link :to="learnMoreHref" class="text-primary-600 hover:text-primary-700 font-medium underline">
              policies
            </router-link>.
          </p>
          <p class="text-sm text-blue-900" v-else>
            For more information about how we use cookies and protect your privacy, please read our
            <router-link to="/privacy-policy" class="text-primary-600 hover:text-primary-700 font-medium underline">
              Privacy Policy
            </router-link>
            and
            <router-link to="/cookie-policy" class="text-primary-600 hover:text-primary-700 font-medium underline">
              Cookie Policy
            </router-link>.
          </p>
        </div>
      </div>

      <!-- Footer -->
      <div class="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex flex-col sm:flex-row gap-3 justify-between">
        <button
          @click="rejectAll"
          class="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
        >
          Reject All
        </button>
        <div class="flex gap-3">
          <button
            @click="acceptAll"
            class="px-6 py-2.5 border border-primary-500 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors font-medium"
          >
            Accept All
          </button>
          <button
            @click="saveCustomPreferences"
            class="px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
          >
            Save My Preferences
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { useCookieConsent, type CookiePreferences } from '@/composables/useCookieConsent'
import { useShopBrand } from '@/composables/useShopBrand'

const emit = defineEmits<{
  close: []
  save: [preferences: CookiePreferences]
}>()

const { preferences, savePreferences, acceptAll: acceptAllCookies, rejectAll: rejectAllCookies } = useCookieConsent()
const { brand, isWhiteLabel } = useShopBrand()

const shopName = computed(() => brand.value?.shopName || 'This site')
const learnMoreHref = computed(() => {
  // WL hosts: clean URL — the host implies the shop, so /about is enough.
  if (isWhiteLabel.value) return '/about'
  return '/privacy-policy'
})

// Local state for the modal (so changes don't apply until user clicks save)
const localPreferences = ref<CookiePreferences>({
  essential: true,
  functional: false,
  analytics: false,
  marketing: false
})

onMounted(() => {
  // Initialize with current preferences
  localPreferences.value = { ...preferences.value }
})

const saveCustomPreferences = async () => {
  await savePreferences(localPreferences.value)
  emit('save', localPreferences.value)
  emit('close')
}

const acceptAll = async () => {
  localPreferences.value = {
    essential: true,
    functional: true,
    analytics: true,
    marketing: true
  }
  await acceptAllCookies()
  emit('save', localPreferences.value)
  emit('close')
}

const rejectAll = async () => {
  localPreferences.value = {
    essential: true,
    functional: false,
    analytics: false,
    marketing: false
  }
  await rejectAllCookies()
  emit('save', localPreferences.value)
  emit('close')
}
</script>
